import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { X, ShieldAlert, Bug, CheckCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { propagateFindingToggle } from '../../../../services/pentestImportService';

const STATUS_OPTIONS = ['non_corrige', 'corrige', 'risque_accepte', 'non_pertinent'];

const confidenceStyle = (level) => {
  switch (level) {
    case 'high':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'medium':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    default:
      return 'bg-red-100 text-red-800 border-red-200';
  }
};

/**
 * PentestReviewModal — user validates AI mapping (asset + fixed/not-fixed) before merge.
 * Findings and attack techniques are grouped by suggested asset; nothing is saved until Confirm.
 */
const PentestReviewModal = ({ isOpen, findings, techniques, detectedAssets, businessAssets, filename, truncated, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [techItems, setTechItems] = useState([]);
  const [expandedMitig, setExpandedMitig] = useState({});
  const [isConfirming, setIsConfirming] = useState(false);

  const resolveTarget = (suggestedAssetId) => (
    suggestedAssetId && (businessAssets || []).some((a) => String(a.id) === String(suggestedAssetId))
      ? String(suggestedAssetId)
      : '__new__'
  );

  useEffect(() => {
    if (isOpen) {
      setItems((findings || []).map((f) => ({
        ...f,
        enabled: f.enabled !== false,
        targetAssetId: resolveTarget(f.suggestedAssetId),
        newAssetName: f.suggestedAssetName || f.title,
        confirmedStatus: f.statusSuggestion === 'corrige' ? 'corrige' : 'non_corrige',
      })));
      setTechItems((techniques || []).map((tech) => ({
        ...tech,
        enabled: tech.enabled !== false,
        targetAssetId: resolveTarget(tech.suggestedAssetId),
        newAssetName: tech.suggestedAssetName || tech.name,
      })));
      setIsConfirming(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, findings, techniques, businessAssets]);

  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = item.targetAssetId === '__new__'
        ? `new:${item.newAssetName || '?'}`
        : String(item.targetAssetId);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return Array.from(map.entries());
  }, [items]);

  if (!isOpen) return null;

  const assetNameOf = (item) => {
    if (item.targetAssetId === '__new__') return item.newAssetName || t('workshop4.pentest.newAsset', 'Nouvel actif');
    const asset = (businessAssets || []).find((a) => String(a.id) === String(item.targetAssetId));
    return asset ? asset.name : t('workshop4.pentest.unknownAsset', 'Actif inconnu');
  };

  const updateItem = (tempId, patch) => {
    // Linked selection: toggling a finding propagates to its suggested techniques
    if (Object.prototype.hasOwnProperty.call(patch, 'enabled')) {
      const propagated = propagateFindingToggle(items, techItems, tempId, patch.enabled);
      setItems(propagated.items.map((it) => (it.tempId === tempId ? { ...it, ...patch } : it)));
      setTechItems(propagated.techItems);
    } else {
      setItems((prev) => prev.map((it) => (it.tempId === tempId ? { ...it, ...patch } : it)));
    }
  };

  const updateTechItem = (tempId, patch) => {
    setTechItems((prev) => prev.map((it) => (it.tempId === tempId ? { ...it, ...patch } : it)));
  };

  const enabledCount = items.filter((i) => i.enabled).length;
  const enabledTechCount = techItems.filter((i) => i.enabled).length;

  const handleConfirm = () => {
    setIsConfirming(true);
    try {
      // Pass ALL items (enabled flags preserved) — unchecked ones persist as pending
      const all = items.map((i) => ({
        ...i,
        targetAssetName: assetNameOf(i),
      }));
      const allTechs = techItems.map((i) => ({
        ...i,
        targetAssetName: assetNameOf(i),
      }));
      onConfirm && onConfirm({ findings: all, techniques: allTechs });
    } finally {
      setIsConfirming(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-slate-800">
              {t('workshop4.pentest.reviewTitle', 'Vérifier les résultats extraits')}
            </h3>
            <p className="text-sm text-slate-600 mt-0.5">
              {filename} — {t('workshop4.pentest.reviewSubtitle', '{{count}} résultats. Corrigez l’affectation et le statut avant de rattacher.', { count: items.length + techItems.length })}
            </p>
            {truncated && (
              <p className="text-xs text-orange-600 mt-1">
                {t('workshop4.pentest.truncated', 'Rapport tronqué à 40 000 caractères : extraction partielle possible.')}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5 bg-slate-50">
          {(detectedAssets || []).length > 0 && (
            <div className="bg-white border border-teal-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-teal-50 border-b border-teal-200 font-semibold text-sm text-teal-900">
                {t('workshop4.networkDiagram.detectedAssets', 'Biens détectés sur le schéma ({{count}})', { count: detectedAssets.length })}
              </div>
              <ul className="divide-y divide-slate-100">
                {(detectedAssets || []).map((asset, ai) => {
                  const matched = (businessAssets || []).find((a) => String(a.id) === String(asset.matchedAssetId));
                  return (
                    <li key={ai} className="px-4 py-2.5 flex items-start gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-slate-800">{asset.name}</span>
                      {asset.exposure && asset.exposure !== 'unknown' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                          {asset.exposure}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${confidenceStyle(asset.confidence)}`}>
                        {t('workshop4.pentest.confidence', 'Confiance')}: {asset.confidence || 'low'}
                      </span>
                      {matched ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-200">
                          → {matched.name}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-200">
                          {t('workshop4.networkDiagram.unmatchedAsset', 'non référencé')}
                        </span>
                      )}
                      {(asset.vendor || asset.product || asset.version) && (
                        <span className="text-xs text-slate-500 w-full">
                          {[asset.vendor, asset.product, asset.version].filter(Boolean).join(' • ')}
                        </span>
                      )}
                      {asset.missingInfo && (
                        <span className="text-xs text-amber-700 italic w-full">⚠ {asset.missingInfo}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {groups.map(([key, groupItems]) => (
            <div key={key} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-100 border-b border-slate-200 font-semibold text-sm text-slate-700 flex items-center justify-between">
                <span>{assetNameOf(groupItems[0])} ({groupItems.length})</span>
                {groupItems[0].targetAssetId === '__new__' && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
                    {t('workshop4.pentest.newAsset', 'Nouvel actif')}
                  </span>
                )}
              </div>
              <div className="divide-y divide-slate-100">
                {groupItems.map((item) => (
                  <div key={item.tempId} className={`p-4 ${item.enabled ? '' : 'opacity-50'}`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={item.enabled}
                        onChange={(e) => updateItem(item.tempId, { enabled: e.target.checked })}
                        className="mt-1.5 h-4 w-4 text-purple-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {item.cveId ? (
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-200">
                              <Bug size={12} /> {item.cveId}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                              <ShieldAlert size={12} /> {t('workshop4.pentest.riskNoCve', 'Risque (sans CVE)')}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${confidenceStyle(item.assetConfidence)}`}>
                            {t('workshop4.pentest.confidence', 'Confiance')}: {item.assetConfidence}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                            {item.severityHint}
                          </span>
                        </div>
                        <p className="font-semibold text-slate-800 mt-1.5">{item.title}</p>
                        <p className="text-sm text-slate-600 mt-0.5 line-clamp-3">{item.description}</p>
                        {item.statusEvidence && (
                          <p className="text-xs text-slate-500 italic mt-1.5 border-l-2 border-slate-200 pl-2">
                            « {item.statusEvidence} »
                          </p>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                          <label className="text-xs text-slate-600">
                            {t('workshop4.pentest.targetAsset', 'Bien support concerné')}
                            <select
                              value={item.targetAssetId}
                              onChange={(e) => updateItem(item.tempId, { targetAssetId: e.target.value })}
                              className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white"
                            >
                              {(businessAssets || []).map((a) => (
                                <option key={a.id} value={String(a.id)}>{a.name}</option>
                              ))}
                              <option value="__new__">{t('workshop4.pentest.newAsset', 'Nouvel actif')}…</option>
                            </select>
                          </label>
                          <label className="text-xs text-slate-600">
                            {t('workshop4.pentest.status', 'Statut (suggestion IA : {{s}})', { s: item.statusSuggestion })}
                            <select
                              value={item.confirmedStatus}
                              onChange={(e) => updateItem(item.tempId, { confirmedStatus: e.target.value })}
                              className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white"
                            >
                              {STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s}>{t(`workshop4.ctiDisplay.${s === 'non_corrige' ? 'notFixed' : s === 'corrige' ? 'fixed' : s === 'risque_accepte' ? 'riskAccepted' : 'notRelevant'}`)}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                        {item.targetAssetId === '__new__' && (
                          <input
                            value={item.newAssetName}
                            onChange={(e) => updateItem(item.tempId, { newAssetName: e.target.value })}
                            placeholder={t('workshop4.pentest.newAssetName', "Nom du nouvel actif")}
                            className="mt-2 w-full text-sm border border-purple-300 rounded-lg px-2 py-1.5"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {items.length === 0 && techItems.length === 0 && (
            <div className="text-center py-10 text-slate-500">
              <AlertTriangle size={28} className="mx-auto mb-2 text-slate-300" />
              {t('workshop4.pentest.noFindings', 'Aucun résultat extrait. Essayez un autre fichier.')}
            </div>
          )}

          {techItems.length > 0 && (
            <div className="bg-white border border-purple-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-purple-50 border-b border-purple-200 font-semibold text-sm text-purple-900">
                {t('workshop4.pentest.techniquesTitle', 'Techniques d’attaque observées ({{count}})', { count: techItems.length })}
              </div>
              <div className="divide-y divide-slate-100">
                {techItems.map((item) => (
                  <div key={item.tempId} className={`p-4 ${item.enabled ? '' : 'opacity-50'}`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={item.enabled}
                        onChange={(e) => updateTechItem(item.tempId, { enabled: e.target.checked })}
                        className="mt-1.5 h-4 w-4 text-purple-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-2 py-0.5 text-xs font-mono font-bold bg-purple-600 text-white rounded">
                            {item.techniqueId}
                          </span>
                          {item.tactic && (
                            <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 border border-indigo-200 rounded">
                              {item.tactic}
                            </span>
                          )}
                          {item.suggested && (
                            <span className="px-2 py-0.5 text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200 rounded">
                              {t('workshop4.pentest.suggestedChip', 'Suggérée')}
                            </span>
                          )}
                          {item.mitreVerified && (
                            <span className="px-2 py-0.5 text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 rounded">
                              {t('workshop4.pentest.verifiedChip', '✓ MITRE')}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${confidenceStyle(item.assetConfidence)}`}>
                            {t('workshop4.pentest.confidence', 'Confiance')}: {item.assetConfidence}
                          </span>
                        </div>
                        <p className="font-semibold text-slate-800 mt-1.5">{item.name}</p>
                        <p className="text-sm text-slate-600 mt-0.5 line-clamp-3">{item.description}</p>
                        {item.rationale && (
                          <p className="text-xs text-amber-700 italic mt-1.5 border-l-2 border-amber-300 pl-2">
                            « {item.rationale} »
                          </p>
                        )}
                        {item.relatedFindingTitle && (
                          <p className="text-xs text-slate-500 mt-1">
                            {t('workshop4.pentest.relatedFinding', 'Résultat lié : {{title}}', { title: item.relatedFindingTitle })}
                          </p>
                        )}
                        {item.mitigations && item.mitigations.length > 0 && (
                          <div className="mt-2">
                            <button
                              onClick={() => setExpandedMitig((prev) => ({ ...prev, [item.tempId]: !prev[item.tempId] }))}
                              className="text-xs font-semibold text-purple-700 hover:underline"
                            >
                              {expandedMitig[item.tempId]
                                ? t('workshop4.pentest.hideMitigations', 'Masquer les mitigations')
                                : t('workshop4.pentest.showMitigations', 'Voir les mitigations ({{count}})', { count: item.mitigations.length })}
                            </button>
                            {expandedMitig[item.tempId] && (
                              <ul className="mt-1 space-y-1">
                                {item.mitigations.map((m, mi) => (
                                  <li key={mi} className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1">
                                    <span className="font-mono font-bold">{m.id}</span>
                                    {' — '}{m.name}
                                    {m.description && (
                                      <span className="block text-slate-600 mt-0.5 line-clamp-2">{m.description}</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                        <label className="text-xs text-slate-600 block mt-3">
                          {t('workshop4.pentest.targetAsset', 'Bien support concerné')}
                          <select
                            value={item.targetAssetId}
                            onChange={(e) => updateTechItem(item.tempId, { targetAssetId: e.target.value })}
                            className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white"
                          >
                            {(businessAssets || []).map((a) => (
                              <option key={a.id} value={String(a.id)}>{a.name}</option>
                            ))}
                            <option value="__new__">{t('workshop4.pentest.newAsset', 'Nouvel actif')}…</option>
                          </select>
                        </label>
                        {item.targetAssetId === '__new__' && (
                          <input
                            value={item.newAssetName}
                            onChange={(e) => updateTechItem(item.tempId, { newAssetName: e.target.value })}
                            placeholder={t('workshop4.pentest.newAssetName', "Nom du nouvel actif")}
                            className="mt-2 w-full text-sm border border-purple-300 rounded-lg px-2 py-1.5"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-between gap-3 bg-white">
          <p className="text-sm text-slate-600 flex items-center gap-1.5">
            <CheckCircle size={16} className="text-green-600" />
            {t('workshop4.pentest.selectedCount', '{{count}} résultat(s) à rattacher', { count: enabledCount + enabledTechCount })}
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700">
              {t('common.cancel', 'Annuler')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={(enabledCount + enabledTechCount) === 0 || isConfirming}
              className="px-4 py-2 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-300 font-medium"
            >
              {t('workshop4.pentest.confirmAttach', 'Confirmer et rattacher')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

PentestReviewModal.propTypes = {
  isOpen: PropTypes.bool,
  findings: PropTypes.array,
  techniques: PropTypes.array,
  detectedAssets: PropTypes.array,
  businessAssets: PropTypes.array,
  filename: PropTypes.string,
  truncated: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};

PentestReviewModal.defaultProps = {
  findings: [],
  techniques: [],
  detectedAssets: [],
  businessAssets: [],
};

export default PentestReviewModal;
